/**
 * Export, once: the one table every format is written from, the sentence that says which rows it
 * holds, the decision of where those rows come from, and the hand-over of the file.
 *
 * docs/list-standard.md §5 is the specification. Four formats and one shape: `tableOf` decides
 * which columns, in which order, under which headers, and what each cell says — and CSV, the
 * workbook, the Word document and the print view only write that table down, the last three on
 * the paper the screen declares (`export.paper`, paper.ts). Two builders would drift, and the day
 * they did the same list would carry two different headers depending on a radio button.
 *
 * Nothing here fetches. On a `query` source the rows come from the route's `/export` (the same
 * query, no page) and the caller hands them in; `exportPlan` is what says so, and it is a pure
 * function so the rule "never the page in view for a paged source" is tested, not hoped for.
 */
import { CLOSED, groupAt, pruned, queryFor, resolveLabel, valueLabel } from "./derive.js";
import { dateDisplay, formatHijriDay } from "./fields.js";
import { isApplicable, isQuick } from "./manifest.js";
import { formatCell, momentOptions, numbersIn, readersValue, rowKey } from "./render.js";
import { workbook } from "./export-xlsx.js";
import { plainWords } from "./i18n.js";
import { printDocument, openPrintView } from "./export-print.js";
import { wordDocument, DOCX_TYPE } from "./export-docx.js";
/**
 * Whether a file carries the Hijri day beside each date (CP-CAL-004: the same event in both
 * calendars on the screen and in the report it produces). Yes whenever the workspace reads its
 * dates in both — `inline` or `tooltip` alike, because a file has no hover to hide the twin behind;
 * no while the workspace has said nothing (`off`), so a workspace without the calendar exports as
 * it always did.
 */
export function hijriInFiles(display = dateDisplay()) {
    return display.hijri !== 'off';
}
/**
 * The day the workspace's records begin, for a file's header (#26): the calendar's setting, handed
 * to the renderer with the date display, or nothing while the workspace has not said it — never a
 * day guessed from the rows. `exportOptions.dataFrom` overrides it.
 */
export function dataFromInFiles(display = dateDisplay()) {
    return display.recordsFrom ?? undefined;
}
const TONES = ['green', 'amber', 'red', 'blue', 'violet', 'neutral'];
const KINDS = { number: 'number', money: 'money', count: 'count', date: 'date', datetime: 'datetime', time: 'time', bytes: 'text', percent: 'number' };
/**
 * The columns a file carries: the ones the screen shows, in the screen's order — the table's heads
 * when `heads` names them, the columns folded under a row left out — or every column the screen
 * declared. A `person` cell draws a second column under the name — the employee number
 * under the employee — so a file of the shown columns carries that one too, beside it: a cell that
 * reads "Sara" in a file of four hundred rows is not the cell the screen showed.
 */
export function exportColumns(manifest, which, heads) {
    const declared = manifest.columns ?? [];
    if (which === 'all')
        return declared;
    const drawn = declared.filter((c) => !c.hidden && (!heads || heads.includes(c.key)));
    const out = [];
    for (const column of drawn) {
        out.push(column);
        const detail = column.display === 'person' && column.detail ? declared.find((c) => c.key === column.detail) : undefined;
        if (detail && !out.includes(detail) && !drawn.includes(detail))
            out.push(detail);
    }
    return out;
}
/**
 * The one shape. Every format writes this and nothing else: the headers translated, each cell as
 * the table shows it (`formatCell`, so a status reads «Active» and never `active`), the raw value
 * beside it for a format that writes numbers as numbers, and a badge's tone. With `hijri`, a date
 * column is followed by the same day in the Hijri calendar, by the workspace's reference.
 */
export function tableOf(rows, columns, ctx, options = {}) {
    const out = [];
    for (const column of columns) {
        const kind = KINDS[column.type ?? 'text'] ?? 'text';
        const label = resolveLabel(column.label, column.label_key, ctx.i18n);
        out.push({
            column: { key: column.key, label, kind, ...(kind === 'money' && column.currency ? { currency: column.currency } : {}) },
            cell: (row) => {
                const raw = row[column.key];
                const tone = column.display === 'badge' && raw !== null && raw !== undefined ? column.tones?.[String(raw)] : undefined;
                return { text: formatCell(readersValue(raw, column, row, ctx.i18n), column, ctx.i18n), raw, ...(tone && TONES.includes(tone) ? { tone: tone } : {}) };
            },
        });
        if (options.hijri && (kind === 'date' || kind === 'datetime')) {
            out.push({
                column: { key: `${column.key}.hijri`, label: `${label} · ${ctx.i18n.t('export.hijri')}`, kind: 'text' },
                cell: (row) => {
                    const raw = row[column.key];
                    const day = kind === 'datetime' ? localDay(raw) : typeof raw === 'string' ? raw : null;
                    // By the workspace's own reference, as the cell drew it: a tabular workspace's file must not
                    // name its days by Umm al-Qura.
                    const text = day ? formatHijriDay(day, dateDisplay().reference, ctx.i18n.locale) : null;
                    return { text: text ?? '—', raw: text };
                },
            });
        }
    }
    const table = { columns: out.map((o) => o.column), rows: rows.map((row) => out.map((o) => o.cell(row))) };
    const summed = new Set((options.totals ?? []).filter((key) => columns.some((c) => c.key === key)));
    if (summed.size) {
        const labelAt = table.columns.findIndex((c) => !summed.has(c.key));
        table.totals = table.columns.map((c, i) => {
            if (summed.has(c.key)) {
                const column = columns.find((x) => x.key === c.key);
                const total = sumOf(rows.map((row) => row[c.key]));
                return { text: total === null ? '' : formatCell(total, column, ctx.i18n), raw: total };
            }
            return i === labelAt ? { text: ctx.i18n.t('export.totals'), raw: null } : { text: '', raw: null };
        });
    }
    return table;
}
/**
 * The sum of a column's values as a number, added in whole units of its finest decimal so money
 * does not drift (0.1 + 0.2 is 0.3 here). Numbers and numeric text count; anything else — a blank,
 * a dash — is left out, as a spreadsheet's SUM leaves it out. Null when nothing was a number.
 */
export function sumOf(values) {
    const parts = [];
    for (const v of values) {
        const text = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : typeof v === 'string' ? v.trim() : '';
        const m = /^(-)?(\d+)(?:\.(\d{1,6}))?$/.exec(text);
        if (!m)
            continue;
        const places = m[3]?.length ?? 0;
        parts.push({ whole: BigInt(`${m[1] ?? ''}${m[2]}${m[3] ?? ''}`), places });
    }
    if (!parts.length)
        return null;
    const places = Math.max(...parts.map((p) => p.places));
    let total = 0n;
    for (const p of parts)
        total += p.whole * 10n ** BigInt(places - p.places);
    return Number(total) / 10 ** places;
}
/** The local day of an instant, as YYYY-MM-DD — the day the screen shows it on. */
function localDay(raw) {
    if (typeof raw !== 'string' || !raw)
        return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()))
        return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** CSV, UTF-8 with a byte-order mark so a spreadsheet opens Arabic as Arabic. Values as the table shows them. */
export function csvOf(table) {
    // A CSV is read back by machines: the isolates that keep a code in its order on screen are taken out.
    const esc = (v) => `"${plainWords(v).replace(/"/g, '""')}"`;
    const head = table.columns.map((c) => esc(c.label)).join(',');
    const body = table.rows.map((r) => r.map((c) => esc(c.text)).join(','));
    const totals = table.totals ? [table.totals.map((c) => esc(c.text)).join(',')] : [];
    return '﻿' + [head, ...body, ...totals].join('\r\n');
}
// ── the sentence about the rows ─────────────────────────────────────────────────────────────
function describeCondition(node, columns, ctx) {
    if ('items' in node) {
        const joiner = ` ${ctx.i18n.t(node.rel === 'any' ? 'list.or' : 'list.and')} `;
        return `(${node.items.map((n) => describeCondition(n, columns, ctx)).join(joiner)})`;
    }
    const column = columns.find((x) => x.key === node.column) ?? { key: node.column, label: node.column };
    const label = resolveLabel(column.label, column.label_key, ctx.i18n);
    const show = (v) => formatCell(v, column, ctx.i18n);
    if (node.op === 'in')
        return `${label}: ${(Array.isArray(node.value) ? node.value : [node.value]).map((v) => valueLabel(column, v, ctx.i18n)).join(ctx.i18n.t('value.separator'))}`;
    if (node.op === 'between')
        return `${label}: ${ctx.i18n.t('list.between.text', { from: show(node.value), to: show(node.value2) })}`;
    if (node.op === 'is')
        return `${label}: ${valueLabel(column, node.value === 'true' ? true : node.value === 'false' ? false : node.value, ctx.i18n)}`;
    return `${label}: ${ctx.i18n.t(`op.${node.op}`)} ${node.op === 'contains' || node.op === 'starts' ? String(node.value) : show(node.value)}`;
}
/** The advanced filter read aloud, as the chips read it: «Status: Active and (Salary: greater than 12,000 or …)». */
export function describeWhere(where, columns, ctx) {
    const tree = pruned(where);
    if (!tree.items.length)
        return '';
    const joiner = ` ${ctx.i18n.t(tree.rel === 'any' ? 'list.or' : 'list.and')} `;
    return tree.items.map((n) => describeCondition(n, columns, ctx)).join(joiner);
}
/**
 * Everything that narrowed the list, one sentence each, in the order the screen applies them: the
 * tab, the search, each quick filter, the advanced filter. What the file says it holds.
 */
export function describeView(manifest, view, ctx) {
    const columns = manifest.columns ?? [];
    const column = (key, label, labelKey) => columns.find((c) => c.key === key) ?? { key, label: label ?? key, ...(labelKey ? { label_key: labelKey } : {}) };
    const out = [];
    const tabs = manifest.tabs;
    const group = groupAt(tabs, view.tab);
    if (tabs?.values && (view.tab === CLOSED || group || (view.tab >= 0 && view.tab < tabs.values.length))) {
        const c = column(tabs.by);
        const value = tabs.values[view.tab];
        const named = view.tab === CLOSED ? ctx.i18n.t('list.closed')
            : group ? resolveLabel(group.label, group.label_key, ctx.i18n)
                : tabs.labels?.[String(value)] ?? valueLabel(c, value, ctx.i18n);
        out.push(`${resolveLabel(c.label, c.label_key, ctx.i18n)}: ${named}`);
    }
    if (view.search.trim())
        out.push(`${ctx.i18n.t('list.search')}: «${view.search.trim()}»`);
    for (const filter of manifest.filters ?? []) {
        // A dimension the screen has not got is written as that — «Shift: not applicable» — rather
        // than left out, which a reader would take for «every shift» (Q-17).
        if (!isApplicable(filter)) {
            out.push(`${resolveLabel(filter.label, filter.label_key, ctx.i18n)}: ${ctx.i18n.t('list.filter.notApplicable')}`);
            continue;
        }
        const chosen = view.filters[filter.column];
        if (!chosen || !isQuick(filter))
            continue;
        const c = column(filter.column, filter.label, filter.label_key);
        out.push(`${resolveLabel(filter.label, filter.label_key, ctx.i18n)}: ${valueLabel(c, chosen, ctx.i18n)}`);
    }
    const where = describeWhere(view.where, columns, ctx);
    if (where)
        out.push(where);
    return out;
}
/** The order as a sentence: «Joined, newest first» is not offered; «Joined — descending» is. */
export function describeSort(manifest, sort, ctx) {
    if (!sort)
        return '';
    const desc = sort.startsWith('-');
    const key = desc ? sort.slice(1) : sort;
    const c = (manifest.columns ?? []).find((x) => x.key === key) ?? { key, label: key };
    return `${resolveLabel(c.label, c.label_key, ctx.i18n)} — ${ctx.i18n.t(desc ? 'export.sort.desc' : 'export.sort.asc')}`;
}
/** The label and value pairs every format writes above its table, in one order. */
export function metaLines(meta, ctx) {
    const t = (key) => ctx.i18n.t(key);
    const at = new Date(meta.at);
    const when = Number.isNaN(at.getTime()) ? meta.at : at.toLocaleString(numbersIn(ctx.i18n.locale), momentOptions({ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }));
    return [
        [t('export.meta.filters'), meta.filters.length ? meta.filters.join(` ${t('list.and')} `) : t('export.meta.filters.none')],
        ...(meta.sort ? [[t('export.meta.sort'), meta.sort]] : []),
        [t('export.meta.rows'), meta.scope],
        [t('export.meta.at'), when],
        [t('export.meta.by'), meta.by?.trim() || t('export.meta.by.unknown')],
        ...(meta.dataFrom ? [[t('export.meta.dataFrom'), meta.dataFrom]] : []),
    ];
}
// ── where the rows come from ────────────────────────────────────────────────────────────────
/** The route's query for a file: the list's query with no page — the whole narrowed view. */
export function exportQuery(manifest, view) {
    const p = new URLSearchParams(queryFor(manifest, view).replace(/^\?/, ''));
    p.delete('page');
    p.delete('size');
    const out = p.toString();
    return out ? `?${out}` : '';
}
/**
 * Where the rows of a file come from. On a complete source everything is in hand: what the view
 * left, the page, or the selection. On a `query` source the rows in hand are one page, so "all
 * results" is asked of the route with the same query and no page — and so is the selection, which
 * survives paging and is therefore more than the page holds: the route answers the view and the
 * selected keys are kept from it. Only "this page" is the page, because that is what it says.
 */
export function exportPlan(manifest, view, scope, have) {
    const serverSide = Boolean(manifest.source?.query) && !manifest.source?.complete;
    if (scope === 'page')
        return { via: 'browser', rows: have.page };
    if (!serverSide)
        return { via: 'browser', rows: scope === 'selected' ? have.selectedRows : have.left };
    return scope === 'selected'
        ? { via: 'route', query: exportQuery(manifest, view), keep: have.selectedKeys }
        : { via: 'route', query: exportQuery(manifest, view) };
}
// ── the file ────────────────────────────────────────────────────────────────────────────────
/** Hand the viewer a file. A plain page downloads; a page that cannot (no document) does nothing. */
export function download(filename, body, type = 'text/csv;charset=utf-8') {
    if (typeof document === 'undefined')
        return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([body], { type }));
    a.download = filename;
    a.click();
    // Revoked on the next turn, not at once: some engines start reading the blob after click returns.
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
}
/**
 * Hand over a file an action answered with. A printable page — HTML, such as an invoice the server
 * drew for printing (#84) — opens in the print view, where the browser's own dialog prints it or
 * saves it as PDF, as the list's PDF export does; every other file downloads. The HTML is shown in
 * a frame of its own document, never in the page, so nothing in it reaches the page.
 */
export async function deliverFile(file) {
    if (/^text\/html\b/i.test(file.type)) {
        const html = typeof file.body === 'string' ? file.body : await new Blob([file.body]).text();
        openPrintView(html);
        return 'printed';
    }
    // A PDF or an image opens in a tab of its own, where the browser previews it (#90) — and saves
    // it from there if the person wants. A browser that refuses the tab gets the file saved instead.
    if (/^(application\/pdf|image\/(png|jpeg|webp|gif))\b/i.test(file.type)) {
        const opener = globalThis.window?.open;
        if (typeof opener === 'function' && typeof URL.createObjectURL === 'function') {
            const url = URL.createObjectURL(new Blob([file.body], { type: file.type }));
            const tab = opener(url, '_blank');
            // Kept long enough for the tab to read it, then let go.
            const later = setTimeout(() => URL.revokeObjectURL(url), 60_000);
            later.unref?.();
            if (tab)
                return 'previewed';
        }
    }
    download(file.name, file.body, file.type);
    return 'downloaded';
}
/**
 * The file in an action's answer (`then: "download"`), or null when the answer is not one. Three
 * shapes reach here, and each is a file only if it says so:
 *
 * - bytes, when the route answered with something other than JSON (`session.send(…, { as: 'file' })`
 *   hands back `{ blob, filename, type }`);
 * - `{ name, media_type, content_base64 }` — the shape the SDK's own `FileInput` sends, and what a
 *   route storing an attachment answers with (leave's evidence);
 * - `{ filename, content_type, content_base64 }` — the shape the government files are answered in.
 *
 * Base64 that does not decode is not a file: null, and the caller says so rather than handing over
 * something that will not open. The name falls back to `fallback`, never to nothing.
 */
export function fileOfAnswer(answer, fallback = 'file') {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer))
        return null;
    const a = answer;
    const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    if (typeof Blob !== 'undefined' && a['blob'] instanceof Blob) {
        const blob = a['blob'];
        return { name: text(a['filename']) ?? fallback, type: text(a['type']) ?? (blob.type || 'application/octet-stream'), body: blob };
    }
    const content = text(a['content_base64']);
    if (!content)
        return null;
    const bytes = fromBase64(content);
    if (!bytes)
        return null;
    return {
        name: text(a['name']) ?? text(a['filename']) ?? fallback,
        type: text(a['media_type']) ?? text(a['content_type']) ?? 'application/octet-stream',
        body: bytes,
    };
}
/** Base64 to bytes, or null when it is not base64. */
function fromBase64(content) {
    const clean = content.replace(/\s+/g, '');
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(clean) || clean.length % 4 === 1)
        return null;
    const decode = globalThis.atob;
    if (!decode)
        return null;
    try {
        const raw = decode(clean);
        const out = new Uint8Array(new ArrayBuffer(raw.length));
        for (let i = 0; i < raw.length; i += 1)
            out[i] = raw.charCodeAt(i);
        return out;
    }
    catch {
        return null;
    }
}
/** The file's base name: the manifest's, or its entity, or the screen key; then the day. */
export function exportFilename(manifest, at, extension) {
    const base = manifest.export?.filename ?? manifest.entity ?? manifest.screen.replace(/\./g, '-');
    return `${base}-${at.slice(0, 10)}.${extension}`;
}
export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/**
 * Build and hand over the file, from rows already chosen — by the list on a complete source, by
 * the caller from the route's `/export` on a query source. One entry for both, so the two kinds
 * of source cannot export two shapes. CSV, XLSX and DOCX download; PDF opens the print view, where the
 * browser's own print dialog saves it (the dialog says so before anybody chooses it).
 */
export async function deliverExport({ manifest, rows, choice, ctx }) {
    const keep = choice.keep ? new Set(choice.keep) : null;
    const chosen = keep ? rows.filter((r, i) => keep.has(rowKey(r, i))) : rows;
    const table = tableOf(chosen, exportColumns(manifest, choice.columns, choice.heads), ctx, { hijri: choice.hijri ?? false, ...(manifest.export?.totals ? { totals: manifest.export.totals } : {}) });
    const meta = choice.meta ?? {
        title: resolveLabel(manifest.title ?? manifest.screen, manifest.title_key, ctx.i18n),
        filters: [], sort: '', scope: String(chosen.length), at: new Date().toISOString(),
    };
    // The count the file states is the count it holds — on a route with the selection kept, fewer
    // than were selected when some no longer match the view.
    const stated = choice.meta ? { ...meta, scope: meta.scope.replace(/\{n\}/g, chosen.length.toLocaleString(numbersIn(ctx.i18n.locale))) } : meta;
    const paper = manifest.export?.paper;
    // The sheet chosen at print time reaches the formats with pages a reader prints; the workbook's
    // page setup stays the screen's (Excel's own page setup changes it).
    const printed = choice.paper ?? paper;
    if (choice.format === 'xlsx') {
        download(exportFilename(manifest, stated.at, 'xlsx'), await workbook(table, stated, ctx, { paper }), XLSX_TYPE);
    }
    else if (choice.format === 'docx') {
        download(exportFilename(manifest, stated.at, 'docx'), await wordDocument(table, stated, ctx, { paper: printed }), DOCX_TYPE);
    }
    else if (choice.format === 'pdf') {
        openPrintView(printDocument(table, stated, ctx, printed));
    }
    else {
        download(exportFilename(manifest, stated.at, 'csv'), csvOf(table));
    }
}
/**
 * What the list writes about a file when its dialog is confirmed. `{n}` in the scope is filled by
 * `deliverExport` with the rows the file holds, which on a route is only known once it answered.
 */
export function metaFor(manifest, view, scope, ctx, by, dataFrom) {
    const scopeKey = scope === 'selected' ? 'list.export.selected' : scope === 'page' ? 'list.export.page' : 'list.export.filtered';
    return {
        title: resolveLabel(manifest.title ?? manifest.screen, manifest.title_key, ctx.i18n),
        filters: describeView(manifest, view, ctx),
        sort: describeSort(manifest, view.sort, ctx),
        scope: ctx.i18n.t(scopeKey, { n: '{n}' }),
        at: new Date().toISOString(),
        ...(by ? { by } : {}),
        ...(dataFrom ? { dataFrom } : {}),
    };
}
//# sourceMappingURL=export.js.map